// src/pages/VehicleV2/FuelConsumption.jsx
// Flow:
// 1) Listing row click -> navigate("/vehicle/FuelConsumption", { state:{ vehicleId, type:"Own"|"Hire" } })
// 2) Entry page reads location.state OR localStorage -> calls API init -> shows fields
// 3) Search -> calls API search (with fromDate/toDate optional)
// 4) Submit -> calls API submit
//
// Backend expected:
//   app.use("/api/fuelConsumption", fuelConsumption);
//
// APIs used:
//   GET  {BASE_URL}/api/fuelConsumption/init?vehicleId=&type=Own|Hire&loginUserId=&branchId=
//   GET  {BASE_URL}/api/fuelConsumption/search?vehicleId=&type=Own|Hire&fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
//   POST {BASE_URL}/api/fuelConsumption/submit
//
// Design aligned with Production BOM Listing / Fuel Consumption Listing:
// - getLS + common headers + VITE_BASE_URL
// - header / card / criteria-panel / result-panel / button styles
// - pageLoading + searching + submitting states

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowLeft,
  FaSearch,
  FaRedo,
  FaPaperPlane,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/fuelConsumption`;

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toNum = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isoToDMY = (iso) => {
  if (!iso) return "";
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const dmyToISO = (dmy) => {
  const m = String(dmy || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${String(m[2]).padStart(2, "0")}-${String(m[1]).padStart(
    2,
    "0"
  )}`;
};

const FuelConsumption = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const getSS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = sessionStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS =
    getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserIdLS = getSS("loginUserId") || userIdLS;

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branch = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId = useMemo(
    () => toInt(loginUserIdLS, 0),
    [loginUserIdLS]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const vehicleId = useMemo(
    () =>
      location?.state?.vehicleId ||
      getLS("FuelConsumptionVehicleId") ||
      "",
    [location?.state?.vehicleId]
  );

  const initialType = useMemo(
    () =>
      location?.state?.type || getLS("FuelConsumptionType") || "Own",
    [location?.state?.type]
  );

  const [vehicleType, setVehicleType] = useState(
    String(initialType).toLowerCase().includes("hire") ? "Hire" : "Own"
  );

  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transportQtyM3, setTransportQtyM3] = useState("");

  const [pageLoading, setPageLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);

  const [vehicle, setVehicle] = useState({
    vehicleRegNo: "",
    fuelTypeName: "",
    fuelTypeId: 0,
    vehicleBasisType: "",
  });

  const [result, setResult] = useState({
    startingDate: "",
    endingDate: "",
    noOfLoads: 0,
    noOfKm: 0,
    noOfHrs: 0,
    fuelRate: 0,
    fuelQty: 0,
    fuelTotalAmount: 0,
    avgLead: 0,
  });

  const isBusy = pageLoading || searching || submitting;

  const computed = useMemo(() => {
    const fuelQty = toNum(result.fuelQty, 0);
    const km = toNum(result.noOfKm, 0);
    const hrs = toNum(result.noOfHrs, 0);

    return {
      fuelConsumPerKm: km > 0 ? fuelQty / km : 0,
      fuelConsumPerHrs: hrs > 0 ? fuelQty / hrs : 0,
    };
  }, [result]);

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const mutedLabelClass = useMemo(
    () => `text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`,
    [isDark]
  );

  const valueClass = useMemo(
    () => `text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`,
    [isDark]
  );

  const Row = ({ label, value, hint }) => (
    <div className="flex items-center justify-between gap-6 py-2">
      <div className={`${mutedLabelClass} w-[46%]`}>{label}</div>
      <div className={`${valueClass} w-[54%] text-left`}>
        {value}
        {hint ? (
          <span
            className={`ml-2 text-xs ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {hint}
          </span>
        ) : null}
      </div>
    </div>
  );

  const resetResult = useCallback(() => {
    setResult({
      startingDate: "",
      endingDate: "",
      noOfLoads: 0,
      noOfKm: 0,
      noOfHrs: 0,
      fuelRate: 0,
      fuelQty: 0,
      fuelTotalAmount: 0,
      avgLead: 0,
    });
  }, []);

  const validateVehicle = useCallback(() => {
    if (!clean(vehicleId)) {
      toast.warn(
        "Vehicle not selected. Please open from listing and click a row."
      );
      return false;
    }
    return true;
  }, [vehicleId]);

  const validateDateRange = useCallback(() => {
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      toast.warn("To Date must be greater than or equal to From Date");
      return false;
    }
    return true;
  }, [fromDate, toDate]);

  const syncVehicleAndResultFromResponse = useCallback((data) => {
    setCanSubmit(Boolean(data?.canSubmit ?? true));

    setVehicle({
      vehicleRegNo: data?.vehicle?.vehicleRegNo || "",
      fuelTypeName:
        data?.vehicle?.fuelTypeName || data?.vehicle?.fuelType || "",
      fuelTypeId: toInt(data?.vehicle?.fuelTypeId, 0),
      vehicleBasisType: data?.vehicle?.vehicleBasisType || "",
    });

    const startISO =
      data?.period?.fromDateISO || data?.period?.startingDateISO || "";
    const endISO =
      data?.period?.toDateISO || data?.period?.endingDateISO || "";

    const startDMY = data?.period?.startingDate || "";
    const endDMY = data?.period?.endingDate || "";

    if (startISO) setFromDate(startISO);
    else if (startDMY) setFromDate(dmyToISO(startDMY));

    if (endISO) setToDate(endISO);
    else if (endDMY) setToDate(dmyToISO(endDMY));

    setResult({
      startingDate: startDMY || (startISO ? isoToDMY(startISO) : ""),
      endingDate: endDMY || (endISO ? isoToDMY(endISO) : ""),
      noOfLoads: toNum(data?.result?.noOfLoads, 0),
      noOfKm: toNum(data?.result?.noOfKm, 0),
      noOfHrs: toNum(data?.result?.noOfHrs, 0),
      fuelRate: toNum(data?.result?.fuelRate, 0),
      fuelQty: toNum(data?.result?.fuelQty, 0),
      fuelTotalAmount: toNum(data?.result?.fuelTotalAmount, 0),
      avgLead: toNum(data?.result?.avgLead, 0),
    });
  }, []);

  const loadInit = useCallback(async () => {
    const ok = validateVehicle();
    if (!ok) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          type: vehicleType,
          loginUserId,
          branchId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      syncVehicleAndResultFromResponse(data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [
    validateVehicle,
    vehicleId,
    vehicleType,
    loginUserId,
    branchId,
    commonHeaders,
    syncVehicleAndResultFromResponse,
  ]);

  const doSearch = useCallback(async () => {
    const okVehicle = validateVehicle();
    if (!okVehicle) return;

    const okDate = validateDateRange();
    if (!okDate) return;

    try {
      setSearching(true);

      const { data } = await axios.get(`${API_BASE}/search`, {
        params: {
          vehicleId,
          type: vehicleType,
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Search failed");
        return;
      }

      syncVehicleAndResultFromResponse(data);
      toast.success("Search completed");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Search failed");
    } finally {
      setSearching(false);
    }
  }, [
    validateVehicle,
    validateDateRange,
    vehicleId,
    vehicleType,
    fromDate,
    toDate,
    commonHeaders,
    syncVehicleAndResultFromResponse,
  ]);

  const doSubmit = useCallback(async () => {
    if (!canSubmit) {
      toast.warn("Submit not allowed (approved / no rights).");
      return;
    }

    const okVehicle = validateVehicle();
    if (!okVehicle) return;

    if (!fromDate || !toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }

    const okDate = validateDateRange();
    if (!okDate) return;

    try {
      setSubmitting(true);

      const payload = {
        vehicleId,
        type: vehicleType,
        loginUserId,
        branchId,
        transportQtyM3: clean(transportQtyM3),
        fromDate,
        toDate,
        startingDateDMY: isoToDMY(fromDate),
        endingDateDMY: isoToDMY(toDate),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Submit failed");
        return;
      }

      toast.success(data?.message || "Saved Successfully");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    validateVehicle,
    fromDate,
    toDate,
    validateDateRange,
    vehicleId,
    vehicleType,
    loginUserId,
    branchId,
    transportQtyM3,
    commonHeaders,
  ]);

  const doReset = useCallback(() => {
    setTransportQtyM3("");
    setFromDate("");
    setToDate("");
    resetResult();
    loadInit();
  }, [resetResult, loadInit]);

  useEffect(() => {
    if (vehicleId) {
      localStorage.setItem("FuelConsumptionVehicleId", String(vehicleId));
    }
    localStorage.setItem("FuelConsumptionType", vehicleType);
  }, [vehicleId, vehicleType]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">FUEL CONSUMPTION</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Fuel Consumption
              </h2>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                INFRA SCREEN : B-18/1
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  canSubmit
                    ? isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                    : isDark
                    ? "bg-gray-700 text-gray-200"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {canSubmit ? "Submit Enabled" : "Submit Disabled"}
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : searching
                    ? "Searching..."
                    : submitting
                    ? "Submitting..."
                    : "Processing..."}
                </span>
              )}

              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          {/* Vehicle summary */}
          <div
            className={`rounded-lg p-4 mb-6 border text-sm ${
              isDark
                ? "bg-gray-800/50 border-gray-700 text-gray-300"
                : "bg-blue-50 border-blue-100 text-gray-700"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                Vehicle ID:{" "}
                <span className="font-semibold">{vehicleId || "-"}</span>
              </div>
              <div>
                Vehicle RegNo:{" "}
                <span className="font-semibold">
                  {vehicle.vehicleRegNo || "-"}
                </span>
              </div>
              <div>
                Fuel Type:{" "}
                <span className="font-semibold">
                  {vehicle.fuelTypeName || "-"}
                </span>
                {vehicle.vehicleBasisType ? (
                  <>
                    {" "}
                    • Basis:{" "}
                    <span className="font-semibold">
                      {vehicle.vehicleBasisType}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* ==================== SEARCH CRITERIA ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={doReset}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <FaRedo />
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={doSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {searching ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Vehicle Type</label>
                  <div className="flex items-center gap-6 h-[42px]">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="vehType"
                        checked={vehicleType === "Hire"}
                        onChange={() => setVehicleType("Hire")}
                        disabled={isBusy}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Hire Vehicle
                      </span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="vehType"
                        checked={vehicleType === "Own"}
                        onChange={() => setVehicleType("Own")}
                        disabled={isBusy}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Own Vehicle
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Transport Qty (M3)</label>
                  <input
                    type="text"
                    value={transportQtyM3}
                    onChange={(e) => setTransportQtyM3(e.target.value)}
                    className={inputClass}
                    placeholder="Enter transport quantity"
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULT AREA ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : (
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 ${
                  isDark ? "bg-gray-800" : "bg-white"
                }`}
              >
                <div
                  className={`p-6 ${
                    isDark ? "border-gray-700" : "border-gray-200"
                  } lg:border-r`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Consumption Details
                  </h3>
                  <Row
                    label="Vehicle Reg.No."
                    value={vehicle.vehicleRegNo || "-"}
                  />
                  <Row
                    label="Starting Date"
                    value={result.startingDate || "-"}
                  />
                  <Row
                    label="No. of Loads"
                    value={toNum(result.noOfLoads).toFixed(2)}
                  />
                  <Row label="Fuel Type" value={vehicle.fuelTypeName || "-"} />
                  <Row
                    label="Fuel Rate"
                    value={toNum(result.fuelRate).toFixed(2)}
                  />
                  <Row
                    label="Fuel Qty"
                    value={toNum(result.fuelQty).toFixed(2)}
                  />
                  <Row
                    label="Fuel Total Amount"
                    value={toNum(result.fuelTotalAmount).toFixed(4)}
                  />
                </div>

                <div className="p-6">
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Usage Summary
                  </h3>
                  <Row label="Ending Date" value={result.endingDate || "-"} />
                  <Row
                    label="No. of KM"
                    value={toNum(result.noOfKm).toFixed(0)}
                  />
                  <Row
                    label="No. of Hrs"
                    value={toNum(result.noOfHrs).toFixed(0)}
                  />
                  <div
                    className={`my-2 border-t ${
                      isDark ? "border-gray-700" : "border-gray-200"
                    }`}
                  />
                  <Row
                    label="Fuel Consum per KM"
                    value={computed.fuelConsumPerKm.toFixed(8)}
                    hint="( Fuel Qty / No.of Km )"
                  />
                  <Row
                    label="Fuel Qty of Hr"
                    value={toNum(result.fuelQty).toFixed(2)}
                  />
                  <Row
                    label="Fuel Consum per Hrs"
                    value={computed.fuelConsumPerHrs.toFixed(6)}
                    hint="( Fuel Qty / No.of Hrs )"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ==================== ACTION BAR ==================== */}
          <div className="p-4 mt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <button
              type="button"
              onClick={doReset}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700"
                  : "border-gray-300 text-gray-800 hover:bg-gray-100"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaRedo />
              Reset
            </button>

            <button
              type="button"
              onClick={doSubmit}
              disabled={isBusy || !canSubmit}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-white ${
                isBusy || !canSubmit
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              <FaPaperPlane />
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default FuelConsumption;